/**
 * Redux slice (src/store/slices/meetingSlice.js).
 *
 * Data flow (typical):
 * - UI dispatches actions/thunks from `src/store/actions/*`.
 * - `reducers` handles synchronous state updates.
 * - `extraReducers` reacts to thunk pending/fulfilled/rejected actions.
 */

import { createSlice } from "@reduxjs/toolkit";
import { generateMeetingToken } from "../actions/meetingActions";

/**
 * Slice initial state.
 *
 * Keep this shape stable because it is consumed across many selectors/components.
 */
const initialState = {
  token: null,
  loading: false,
  error: null,
  
  // Waiting room state
  waitingRoom: {
    isActive: false,
    status: 'idle', // idle, connecting, waiting, admitted, error
    teacherPresent: false,
    waitingTime: 0,
    connectionQuality: 'unknown',
    deviceTests: {
      camera: 'pending',
      microphone: 'pending',
      speakers: 'pending'
    },
    participants: [],
    admissionData: null
  }
};

/**
 * RTK slice definition: meetingSlice.
 */
const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    // Waiting room actions
    /**
     * Reducer: setWaitingRoomActive.
     */
    setWaitingRoomActive: (state, action) => {
      state.waitingRoom.isActive = action.payload;
    },
    
    /**
     * Reducer: setWaitingRoomStatus.
     */
    setWaitingRoomStatus: (state, action) => {
      state.waitingRoom.status = action.payload;
    },
    
    /**
     * Reducer: setTeacherPresent.
     */
    setTeacherPresent: (state, action) => {
      state.waitingRoom.teacherPresent = action.payload;
    },
    
    /**
     * Reducer: updateWaitingTime.
     */
    updateWaitingTime: (state, action) => {
      state.waitingRoom.waitingTime = action.payload;
    },
    
    /**
     * Reducer: setConnectionQuality.
     */
    setConnectionQuality: (state, action) => {
      state.waitingRoom.connectionQuality = action.payload;
    },
    
    /**
     * Reducer: updateDeviceTest.
     */
    updateDeviceTest: (state, action) => {
      const { device, status } = action.payload;
      state.waitingRoom.deviceTests[device] = status;
    },
    
    /**
     * Reducer: setWaitingParticipants.
     */
    setWaitingParticipants: (state, action) => {
      state.waitingRoom.participants = action.payload;
    },
    
    /**
     * Reducer: addWaitingParticipant.
     */
    addWaitingParticipant: (state, action) => {
      state.waitingRoom.participants.push(action.payload);
    },
    
    /**
     * Reducer: removeWaitingParticipant.
     */
    removeWaitingParticipant: (state, action) => {
      state.waitingRoom.participants = state.waitingRoom.participants.filter(
        p => p.userId !== action.payload
      );
    },
    
    /**
     * Reducer: setAdmissionData.
     */
    setAdmissionData: (state, action) => {
      state.waitingRoom.admissionData = action.payload;
    },
    
    /**
     * Reducer: resetWaitingRoom.
     */
    resetWaitingRoom: (state) => {
      state.waitingRoom = initialState.waitingRoom;
    }
  },
  /**
   * Reducer: extraReducers.
   */
  extraReducers: (builder) => {
    builder
      // extraReducers handler: generateMeetingToken.pending
      .addCase(generateMeetingToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      // extraReducers handler: generateMeetingToken.fulfilled
      .addCase(generateMeetingToken.fulfilled, (state, action) => {
        state.loading = false;
        state.token = action.payload;
      })
      // extraReducers handler: generateMeetingToken.rejected
      .addCase(generateMeetingToken.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

// Export action creators
export const {
  setWaitingRoomActive,
  setWaitingRoomStatus,
  setTeacherPresent,
  updateWaitingTime,
  setConnectionQuality,
  updateDeviceTest,
  setWaitingParticipants,
  addWaitingParticipant,
  removeWaitingParticipant,
  setAdmissionData,
  resetWaitingRoom
} = meetingSlice.actions;

export default meetingSlice.reducer;